import React, { useContext, useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import brokersFailPdf from '../Landing/assets/22-reasons-why-brokers-fail.pdf';
import webinarHeroStockImage from '../Landing/assets/22-reasons-brokers-fail-cover.png';
import { FirebaseContext } from '../Firebase';
import '../LandingCopy/styles.css';
import './styles.css';

const EBOOK_DOWNLOAD_PATH = brokersFailPdf;
const DEFAULT_WEBINAR_SESSION = 'march-3-2026-4pm';
const WEBINAR_THANK_YOU_PATH = '/webinar-thank-you';
const WEBINAR_SESSION_LABELS = {
  'march-3-2026-4pm': 'March 3, 2026 - 4:00 PM',
  'march-10-2026-4pm': 'March 10, 2026 - 4:00 PM',
  'either-session': 'Either session works',
  waitlist: 'Waitlist me if sessions are full',
};
// Webinar-specific EmailJS credentials (env first, fallback values to avoid production bypass if env is missing at build time)
const WEBINAR_EMAILJS_PUBLIC_KEY =
  process.env.REACT_APP_WEBINAR_EMAILJS_PUBLIC_KEY || 'q4i-rLyFZYwahiH4b';
const WEBINAR_EMAILJS_SERVICE_ID =
  process.env.REACT_APP_WEBINAR_EMAILJS_SERVICE_ID || 'service_by4gwgv';
const WEBINAR_EMAILJS_TEMPLATE_ID =
  process.env.REACT_APP_WEBINAR_EMAILJS_TEMPLATE_ID || 'template_5mfryck';
const FULL_NAME_PATTERN = /^(?=.{2,80}$)[A-Za-z]+(?:[ .'-][A-Za-z]+)*$/;
const INDIAN_MOBILE_PATTERN = /^[6-9]\d{9}$/;
const FULL_NAME_PATTERN_HTML = "[A-Za-z]+(?:[ .'-][A-Za-z]+)*";
const PHONE_PATTERN_HTML = "(?:\\+?91[\\s-]?)?[6-9][0-9]{9}";
const FULL_NAME_VALIDATION_MESSAGE = 'Please enter your full name using letters only.';
const PHONE_VALIDATION_MESSAGE = 'Please enter a valid 10-digit Indian mobile number (starts with 6-9).';

const normalizeFullName = (value = '') => String(value).replace(/\s+/g, ' ').trim();

const isValidFullName = (value = '') => FULL_NAME_PATTERN.test(normalizeFullName(value));

const normalizeIndianMobileDigits = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  return '';
};

const isValidIndianMobile = (value = '') => INDIAN_MOBILE_PATTERN.test(normalizeIndianMobileDigits(value));

const formatIndianMobile = (value = '') => {
  const digits = normalizeIndianMobileDigits(value);
  return digits ? `+91${digits}` : '';
};

const normalizeEmail = (value = '') => String(value).trim().toLowerCase();

const setFieldError = (field, message) => {
  if (!field) return;
  field.setCustomValidity(message);
  field.reportValidity();
};

const clearFieldError = (field) => {
  if (!field) return;
  field.setCustomValidity('');
};

const WebinarPage = () => {
  const firebase = useContext(FirebaseContext);
  const [isLightMode, setIsLightMode] = useState(true);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [isEbookModalOpen, setIsEbookModalOpen] = useState(false);
  const [ebookAlsoRegister, setEbookAlsoRegister] = useState(true);
  const [ebookPreferredSession, setEbookPreferredSession] = useState(DEFAULT_WEBINAR_SESSION);
  const [isEbookModalSubmitting, setIsEbookModalSubmitting] = useState(false);
  const [ebookModalSubmitStatus, setEbookModalSubmitStatus] = useState(null); // 'success', 'error', null
  const [ebookName, setEbookName] = useState('');
  const [ebookPhone, setEbookPhone] = useState('');
  const [ebookEmail, setEbookEmail] = useState('');
  const [submissionToast, setSubmissionToast] = useState(null);
  
  // Initialize EmailJS
  useEffect(() => {
    if (!WEBINAR_EMAILJS_PUBLIC_KEY) return;
    emailjs.init(WEBINAR_EMAILJS_PUBLIC_KEY);
  }, []);
  
  // Custom cursor with smooth performance
  useEffect(() => {
    const cursorDot = document.querySelector('.cursor-dot');
    const cursorRing = document.querySelector('.cursor-ring');
    
    if (!cursorDot || !cursorRing) return;
    
    let mouseX = 0;
    let mouseY = 0;
    let ringX = 0;
    let ringY = 0;
    
    // Update mouse position
    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      
      // Update dot immediately for crisp tracking
      cursorDot.style.left = `${mouseX}px`;
      cursorDot.style.top = `${mouseY}px`;
    };
    
    // Animate ring with smooth following
    const animateRing = () => {
      ringX += (mouseX - ringX) * 0.15;
      ringY += (mouseY - ringY) * 0.15;
      
      cursorRing.style.left = `${ringX}px`;
      cursorRing.style.top = `${ringY}px`;
      
      requestAnimationFrame(animateRing);
    };
    
    // Hover effects
    const hoverElements = document.querySelectorAll('a, button, .benefit-card, .process-step, .mentor-image, input, textarea, select');
    
    const handleMouseEnter = () => cursorRing.classList.add('hover');
    const handleMouseLeave = () => cursorRing.classList.remove('hover');
    
    hoverElements.forEach(el => {
      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);
    });
    
    document.addEventListener('mousemove', handleMouseMove);
    const animationId = requestAnimationFrame(animateRing);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
      hoverElements.forEach(el => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, []);
  
  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.querySelector('.navbar');
      if (window.scrollY > 100) {
        navbar?.classList.add('scrolled');
      } else {
        navbar?.classList.remove('scrolled');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setIsMobileNavOpen(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Theme toggle
  useEffect(() => {
    const savedTheme = localStorage.getItem('webinar-theme') || 'light';
    if (savedTheme === 'light') {
      document.body.classList.add('light-mode');
      setIsLightMode(true);
    } else {
      document.body.classList.remove('light-mode');
      setIsLightMode(false);
    }
  }, []);

  useEffect(() => {
    if (!isEbookModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsEbookModalOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEbookModalOpen]);

  useEffect(() => {
    if (!submissionToast) return undefined;

    const timerId = setTimeout(() => {
      setSubmissionToast(null);
    }, 7000);

    return () => clearTimeout(timerId);
  }, [submissionToast]);
  
  const toggleTheme = () => {
    const newMode = !isLightMode;
    setIsLightMode(newMode);
    
    if (newMode) {
      document.body.classList.add('light-mode');
      localStorage.setItem('webinar-theme', 'light');
    } else {
      document.body.classList.remove('light-mode');
      localStorage.setItem('webinar-theme', 'dark');
    }
  };
  
  // Smooth scroll
  const scrollToSection = (sectionId) => {
    setIsMobileNavOpen(false);
    const section = document.querySelector(sectionId);
    if (section) {
      const offsetTop = section.offsetTop - 100;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  const openEbookModal = () => {
    setIsMobileNavOpen(false);
    setEbookAlsoRegister(true);
    setEbookPreferredSession(DEFAULT_WEBINAR_SESSION);
    setEbookModalSubmitStatus(null);
    setEbookName('');
    setEbookPhone('');
    setEbookEmail('');
    setIsEbookModalOpen(true);
  };

  const triggerEbookDownload = () => {
    const link = document.createElement('a');
    link.href = EBOOK_DOWNLOAD_PATH;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const showSubmissionToast = (message) => {
    setSubmissionToast({
      message,
      id: Date.now(),
    });
  };

  const preopenThankYouTab = (shouldOpen) => {
    if (!shouldOpen || typeof window === 'undefined') return null;
    try {
      return window.open('', '_blank');
    } catch (error) {
      console.warn('Could not preopen thank you tab:', error);
      return null;
    }
  };

  const openThankYouTrackingPage = (tabRef, source) => {
    const thankYouUrl = `${WEBINAR_THANK_YOU_PATH}?source=${encodeURIComponent(source)}`;

    if (tabRef && !tabRef.closed) {
      try {
        tabRef.location.href = thankYouUrl;
        try {
          tabRef.opener = null;
        } catch (openerError) {
          console.warn('Could not clear thank you tab opener:', openerError);
        }
        return;
      } catch (error) {
        console.warn('Could not navigate preopened thank you tab:', error);
      }
    }

    try {
      window.open(thankYouUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.warn('Could not open thank you tracking page:', error);
    }
  };

  const closePreopenedTab = (tabRef) => {
    if (!tabRef || tabRef.closed) return;
    try {
      tabRef.close();
    } catch (error) {
      console.warn('Could not close preopened tab:', error);
    }
  };

  const getWebinarSessionLabel = (sessionCode) =>
    WEBINAR_SESSION_LABELS[sessionCode] || sessionCode || 'Session preference not specified';

  const sendRegistrationConfirmationEmail = async ({
    fullName,
    email,
    phone,
    sessionCode,
    source,
  }) => {
    if (!email) return;
    if (!firebase?.functions?.httpsCallable) {
      console.info('Firebase callable not available. Skipping webinar confirmation email.');
      return;
    }

    try {
      const sendConfirmation = firebase.functions.httpsCallable('sendWebinarRegistrationConfirmation');
      await sendConfirmation({
        fullName: fullName || '',
        email,
        phone: phone || '',
        sessionCode: sessionCode || '',
        sessionLabel: getWebinarSessionLabel(sessionCode),
        source,
      });
    } catch (error) {
      console.warn('Webinar confirmation email send failed (non-blocking):', error);
    }
  };

  const handleEbookModalContinue = async (event) => {
    event.preventDefault();
    if (isEbookModalSubmitting) return;

    const form = event.currentTarget;
    const nameField = form.querySelector('#ebookName');
    const phoneField = form.querySelector('#ebookPhone');
    const emailField = form.querySelector('#ebookEmail');
    const normalizedName = normalizeFullName(ebookName);
    const normalizedPhone = formatIndianMobile(ebookPhone);
    const normalizedEmail = normalizeEmail(ebookEmail);

    clearFieldError(nameField);
    clearFieldError(phoneField);
    clearFieldError(emailField);

    if (!isValidFullName(normalizedName)) {
      setFieldError(nameField, FULL_NAME_VALIDATION_MESSAGE);
      return;
    }

    if (!isValidIndianMobile(ebookPhone)) {
      setFieldError(phoneField, PHONE_VALIDATION_MESSAGE);
      return;
    }

    if (!emailField?.checkValidity()) {
      emailField?.reportValidity();
      return;
    }

    setEbookName(normalizedName);
    setEbookPhone(normalizedPhone);
    setEbookEmail(normalizedEmail);

    const thankYouTab = preopenThankYouTab(ebookAlsoRegister);
    setIsEbookModalSubmitting(true);
    setEbookModalSubmitStatus(null);

    try {
      if (!WEBINAR_EMAILJS_PUBLIC_KEY || !WEBINAR_EMAILJS_SERVICE_ID || !WEBINAR_EMAILJS_TEMPLATE_ID) {
        throw new Error('Missing webinar EmailJS credentials.');
      }

      const interestValue = ebookAlsoRegister ? ebookPreferredSession : 'ebook-only';
      const messageValue = ebookAlsoRegister
        ? `Ebook modal submission: requested free webinar seat (${ebookPreferredSession}) and ebook download.`
        : 'Ebook modal submission: ebook download only. Webinar not requested.';

      await emailjs.send(
        WEBINAR_EMAILJS_SERVICE_ID,
        WEBINAR_EMAILJS_TEMPLATE_ID,
        {
          firstName: normalizedName,
          lastName: '',
          email: normalizedEmail,
          phone: normalizedPhone,
          interest: interestValue,
          experience: 'not-shared (ebook modal)',
          message: messageValue,
          campaign: 'webinar-ebook-modal',
          page: '/webinar',
        },
        { publicKey: WEBINAR_EMAILJS_PUBLIC_KEY }
      );

      triggerEbookDownload();
      if (ebookAlsoRegister) {
        openThankYouTrackingPage(thankYouTab, 'ebook-modal-webinar-checked');
        await sendRegistrationConfirmationEmail({
          fullName: normalizedName,
          email: normalizedEmail,
          phone: normalizedPhone,
          sessionCode: ebookPreferredSession,
          source: 'ebook-modal-webinar-checked',
        });
      }
      setEbookModalSubmitStatus(null);
      setIsEbookModalOpen(false);
      showSubmissionToast(
        ebookAlsoRegister
          ? 'Submitted successfully. Your ebook download has started and your free webinar seat request has been received.'
          : 'Submitted successfully. Your ebook download has started.'
      );
    } catch (error) {
      closePreopenedTab(thankYouTab);
      console.error('Ebook modal EmailJS error:', error);
      setEbookModalSubmitStatus('error');
    } finally {
      setIsEbookModalSubmitting(false);
    }
  };

  const handleWebinarContactSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const form = e.target;
    const fullNameField = form.firstName;
    const phoneField = form.phone;
    const emailField = form.email;
    const normalizedName = normalizeFullName(fullNameField?.value || '');
    const normalizedPhone = formatIndianMobile(phoneField?.value || '');
    const normalizedEmail = normalizeEmail(emailField?.value || '');

    clearFieldError(fullNameField);
    clearFieldError(phoneField);
    clearFieldError(emailField);

    if (!isValidFullName(normalizedName)) {
      setFieldError(fullNameField, FULL_NAME_VALIDATION_MESSAGE);
      return;
    }

    if (!isValidIndianMobile(phoneField?.value || '')) {
      setFieldError(phoneField, PHONE_VALIDATION_MESSAGE);
      return;
    }

    if (!emailField?.checkValidity()) {
      emailField?.reportValidity();
      return;
    }

    if (fullNameField) fullNameField.value = normalizedName;
    if (phoneField) phoneField.value = normalizedPhone;
    if (emailField) emailField.value = normalizedEmail;

    const registrationPayload = {
      fullName: normalizedName,
      email: normalizedEmail,
      phone: normalizedPhone,
      sessionCode: form.interest?.value || '',
      source: 'webinar-registration-form',
    };
    const thankYouTab = preopenThankYouTab(true);
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!WEBINAR_EMAILJS_PUBLIC_KEY || !WEBINAR_EMAILJS_SERVICE_ID || !WEBINAR_EMAILJS_TEMPLATE_ID) {
        throw new Error('Missing webinar EmailJS credentials.');
      }

      const result = await emailjs.sendForm(
        WEBINAR_EMAILJS_SERVICE_ID,
        WEBINAR_EMAILJS_TEMPLATE_ID,
        form,
        { publicKey: WEBINAR_EMAILJS_PUBLIC_KEY }
      );

      console.log('EmailJS Success:', result);
      openThankYouTrackingPage(thankYouTab, 'webinar-registration-form');
      await sendRegistrationConfirmationEmail(registrationPayload);
      setSubmitStatus(null);
      form.reset();
      showSubmissionToast(
        'Thank you. Your webinar registration request has been submitted. Confirmation details will be shared after review.'
      );
    } catch (error) {
      closePreopenedTab(thankYouTab);
      console.error('EmailJS Error Details:', error);
      console.error('Error text:', error.text);
      console.error('Error status:', error.status);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="luxury-landing webinar-landing">
      {/* CORNER ORNAMENTS */}
      <div className="corner-ornament top-left"></div>
      <div className="corner-ornament top-right"></div>
      <div className="corner-ornament bottom-left"></div>
      <div className="corner-ornament bottom-right"></div>

      {isEbookModalOpen && (
        <div className="ebook-modal-backdrop" onClick={() => setIsEbookModalOpen(false)}>
          <div
            className="ebook-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ebook-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="ebook-modal-close"
              onClick={() => setIsEbookModalOpen(false)}
              aria-label="Close ebook popup"
            >
              X
            </button>
            <div className="ebook-modal-overline">Free Ebook Download | Limited Seats for Webinar</div>
            <h3 id="ebook-modal-title" className="ebook-modal-title">
              22 Reasons Why Real Estate Brokers Fail
            </h3>
            <p className="ebook-modal-description">
              Download the ebook now. Webinar registration is optional, and the webinar is free. Seats are limited for both sessions.
            </p>

            <form className="ebook-modal-form" onSubmit={handleEbookModalContinue}>
              <div className="ebook-modal-grid">
                <div className="ebook-modal-field">
                  <label htmlFor="ebookName" className="ebook-modal-label">
                    Name <span className="required-star" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="ebookName"
                    name="ebookName"
                    type="text"
                    className="ebook-modal-input"
                    placeholder="Enter your full name"
                    autoComplete="name"
                    inputMode="text"
                    pattern={FULL_NAME_PATTERN_HTML}
                    title={FULL_NAME_VALIDATION_MESSAGE}
                    maxLength={80}
                    value={ebookName}
                    onChange={(e) => {
                      clearFieldError(e.target);
                      setEbookName(e.target.value);
                    }}
                    onBlur={(e) => setEbookName(normalizeFullName(e.target.value))}
                    required
                  />
                </div>
                <div className="ebook-modal-field">
                  <label htmlFor="ebookPhone" className="ebook-modal-label">
                    Phone Number <span className="required-star" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="ebookPhone"
                    name="ebookPhone"
                    type="tel"
                    className="ebook-modal-input"
                    placeholder="+91 XXX XXX XXXX"
                    autoComplete="tel-national"
                    inputMode="numeric"
                    pattern={PHONE_PATTERN_HTML}
                    title={PHONE_VALIDATION_MESSAGE}
                    maxLength={16}
                    value={ebookPhone}
                    onChange={(e) => {
                      clearFieldError(e.target);
                      setEbookPhone(e.target.value.replace(/[^\d+\s-]/g, ''));
                    }}
                    onBlur={(e) => {
                      const trimmed = e.target.value.trim();
                      const formatted = formatIndianMobile(trimmed);
                      setEbookPhone(formatted || trimmed);
                    }}
                    required
                  />
                </div>
                <div className="ebook-modal-field ebook-modal-field-full">
                  <label htmlFor="ebookEmail" className="ebook-modal-label">
                    Email Address <span className="required-star" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="ebookEmail"
                    name="ebookEmail"
                    type="email"
                    className="ebook-modal-input"
                    placeholder="your@email.com"
                    autoComplete="email"
                    value={ebookEmail}
                    onChange={(e) => {
                      clearFieldError(e.target);
                      setEbookEmail(e.target.value);
                    }}
                    onBlur={(e) => setEbookEmail(normalizeEmail(e.target.value))}
                    required
                  />
                </div>
              </div>

              <label className="ebook-option-card">
                <input
                  type="checkbox"
                  checked={ebookAlsoRegister}
                  onChange={(e) => setEbookAlsoRegister(e.target.checked)}
                />
                <span>
                  Also reserve my free webinar seat (Seats are limited)
                </span>
              </label>

              <div className={`ebook-session-picker ${ebookAlsoRegister ? 'enabled' : 'disabled'}`}>
                <label className="ebook-session-option">
                  <input
                    type="radio"
                    name="ebook-webinar-session"
                    value="march-3-2026-4pm"
                    checked={ebookPreferredSession === 'march-3-2026-4pm'}
                    onChange={(e) => setEbookPreferredSession(e.target.value)}
                    disabled={!ebookAlsoRegister}
                  />
                  <span>March 3, 2026 | 4:00 PM</span>
                </label>
                <label className="ebook-session-option">
                  <input
                    type="radio"
                    name="ebook-webinar-session"
                    value="march-10-2026-4pm"
                    checked={ebookPreferredSession === 'march-10-2026-4pm'}
                    onChange={(e) => setEbookPreferredSession(e.target.value)}
                    disabled={!ebookAlsoRegister}
                  />
                  <span>March 10, 2026 | 4:00 PM</span>
                </label>
              </div>

              <p className="ebook-modal-note">
                Seats are limited for each webinar session. Early requests get priority review.
              </p>

              <div className="ebook-modal-actions">
                <button
                  type="button"
                  className="ebook-modal-secondary"
                  onClick={() => setIsEbookModalOpen(false)}
                  disabled={isEbookModalSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="ebook-modal-primary" disabled={isEbookModalSubmitting}>
                  {isEbookModalSubmitting
                    ? 'Submitting...'
                    : ebookAlsoRegister
                      ? 'Download Ebook + Reserve Free Webinar Seat'
                      : 'Download Ebook only'}
                </button>
              </div>

              {ebookModalSubmitStatus === 'error' && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px 14px',
                  border: '1px solid rgba(231, 76, 60, 0.35)',
                  background: 'rgba(231, 76, 60, 0.08)',
                  color: '#b03a2e',
                  fontSize: '13px',
                  lineHeight: 1.4,
                }}>
                  Could not submit this popup right now. Please try again.
                </div>
              )}
            </form>

            <p className="ebook-modal-note">
              Ebook download opens in a new tab. Webinar registration remains optional. Seats are limited and subject to availability.
            </p>
          </div>
        </div>
      )}

      {submissionToast && (
        <div className="webinar-submit-toast" role="status" aria-live="polite" key={submissionToast.id}>
          <div className="webinar-submit-toast-label">Submitted</div>
          <div className="webinar-submit-toast-message">{submissionToast.message}</div>
        </div>
      )}

      {/* NAVBAR */}
      <nav className="navbar">
        <a href="#" className="navbar-logo" onClick={(e) => { e.preventDefault(); setIsMobileNavOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          <img 
            src={isLightMode ? "/d_logo-removebg-preview.png" : "/Douglas2-removebg-preview.png"} 
            alt="Douglas & McCartney" 
            className="navbar-logo-img"
          />
        </a>
        <button
          type="button"
          className={`mobile-nav-toggle ${isMobileNavOpen ? 'is-open' : ''}`}
          aria-expanded={isMobileNavOpen}
          aria-controls="webinar-mobile-nav-panel"
          aria-label={isMobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          onClick={() => setIsMobileNavOpen((prev) => !prev)}
        >
          <span className="mobile-nav-toggle-line"></span>
          <span className="mobile-nav-toggle-line"></span>
          <span className="mobile-nav-toggle-line"></span>
        </button>
        <ul className="navbar-menu">
          <li><a href="#benefits" className="navbar-link" onClick={(e) => { e.preventDefault(); scrollToSection('#benefits'); }}>Why Attend</a></li>
          <li><a href="#mentor" className="navbar-link" onClick={(e) => { e.preventDefault(); scrollToSection('#mentor'); }}>Speaker</a></li>
          <li><a href="#process" className="navbar-link" onClick={(e) => { e.preventDefault(); scrollToSection('#process'); }}>How It Works</a></li>
          <li><a href="#contact" className="navbar-link" onClick={(e) => { e.preventDefault(); scrollToSection('#contact'); }}>Access</a></li>
        </ul>
        <div className="navbar-actions">
          <button className={`theme-toggle ${isLightMode ? 'active' : ''}`} onClick={toggleTheme} title="Toggle Dark/Light Mode">
            <div className="theme-toggle-circle">
              <span className="theme-toggle-icon">{isLightMode ? 'L' : 'D'}</span>
            </div>
          </button>
          <button className="navbar-button login" onClick={openEbookModal}>Download Free Ebook</button>
          <button className="navbar-button signup" onClick={() => scrollToSection('#contact')}>Reserve Free Seat</button>
        </div>
        <div
          id="webinar-mobile-nav-panel"
          className={`mobile-nav-panel ${isMobileNavOpen ? 'is-open' : ''}`}
        >
          <div className="mobile-nav-links">
            <button type="button" className="mobile-nav-link" onClick={() => scrollToSection('#benefits')}>Why Attend</button>
            <button type="button" className="mobile-nav-link" onClick={() => scrollToSection('#mentor')}>Speaker</button>
            <button type="button" className="mobile-nav-link" onClick={() => scrollToSection('#process')}>How It Works</button>
            <button type="button" className="mobile-nav-link" onClick={() => scrollToSection('#contact')}>Access</button>
          </div>
          <div className="mobile-nav-theme-row">
            <span className="mobile-nav-theme-label">Theme</span>
            <button className={`theme-toggle ${isLightMode ? 'active' : ''}`} onClick={toggleTheme} title="Toggle Dark/Light Mode">
              <div className="theme-toggle-circle">
                <span className="theme-toggle-icon">{isLightMode ? 'L' : 'D'}</span>
              </div>
            </button>
          </div>
          <div className="mobile-nav-actions">
            <button className="navbar-button login" onClick={openEbookModal}>Download Free Ebook</button>
            <button className="navbar-button signup" onClick={() => scrollToSection('#contact')}>Reserve Free Seat</button>
          </div>
        </div>
      </nav>

      {/* HERO SECTION - LEFT ALIGNED & MINIMALISTIC */}
      <section className="hero-section">
        {/* ANIMATED GRID */}
        <div className="hero-grid">
          <div className="hero-grid-line vertical"></div>
          <div className="hero-grid-line vertical"></div>
          <div className="hero-grid-line vertical"></div>
          <div className="hero-grid-line vertical"></div>
          <div className="hero-grid-line horizontal"></div>
          <div className="hero-grid-line horizontal"></div>
          <div className="hero-grid-line horizontal"></div>
          <div className="hero-grid-line horizontal"></div>
        </div>

        {/* FLOATING YEAR MARKS */}
        <div className="hero-year top-left">03 MAR</div>
        <div className="hero-year bottom-right">10 MAR</div>

        {/* SIDE LABELS */}
        <div className="hero-side-label left">Bangalore | India</div>
        <div className="hero-side-label right">Private 1-Hour Session</div>

        {/* MAIN CONTENT - LEFT ALIGNED */}
        <div className="hero-content">
          <div className="hero-overline">Free Webinar | Seats Limited | Optional Free Ebook Download</div>
          <h1 className="hero-title">
            <span className="hero-title-line">Private Webinar for</span>
            <span className="hero-title-line">Real Estate Brokers</span>
          </h1>
          <p className="hero-subtitle">Learn how brokers are building stronger personal brands, attracting better clients, and creating predictable client flow. Seats are limited for each session.</p>
          <div className="hero-cta">
            <button className="hero-button" onClick={() => scrollToSection('#contact')}>
              Reserve Free Webinar Seat
            </button>
            <button
              className="hero-button hero-mobile-ebook-button"
              onClick={openEbookModal}
            >
              Download Free Ebook
            </button>
            <div className="hero-divider"></div>
            <div className="hero-scroll-indicator" onClick={() => scrollToSection('#process')}>
              <span>Free webinar | Limited seats | March 3 &amp; 10, 2026 | 4 PM</span>
              <div className="scroll-arrow"></div>
            </div>
          </div>
        </div>

        <div className="webinar-hero-visual" aria-hidden="true">
          <div className="webinar-hero-card">
            <div className="webinar-hero-badge">Free Private Webinar</div>
            <img
              src={webinarHeroStockImage}
              alt=""
              className="webinar-hero-image"
              loading="eager"
              decoding="async"
            />
            <div className="webinar-hero-overlay">
              <div className="webinar-hero-overlay-label">Sessions</div>
              <div className="webinar-hero-overlay-dates">March 3 | March 10</div>
              <div className="webinar-hero-overlay-time">4:00 PM</div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS SECTION - LEFT ALIGNED HEADERS */}
      <section className="benefits-section" id="benefits">
        <div className="benefits-hero">
          <span className="overline">Webinar Access</span>
          <h1 className="benefits-title">Why This Webinar<br></br>Matters</h1>
          <p className="benefits-subtitle">The invites lead with the free ebook. This page keeps that promise, but shifts the focus to the private webinar and the outcomes brokers care about most. Seats are limited for each session.</p>
        </div>

        <div className="benefits-cards">
          {/* Card 1 */}
          <div className="benefit-card reveal">
            <div className="card-number">01</div>
            <div className="card-label">Pain Point</div>
            <h3 className="card-title">Not getting paid after closing deals</h3>
            <p className="card-description">Why payment leakage happens after the hard work is done, and what brokers can do to protect collections, agreements, and follow-through.</p>
            <div className="card-stats">
              <div className="stat">
                <div className="stat-value">01</div>
                <div className="stat-label">Key Topic</div>
              </div>
              <div className="stat">
                <div className="stat-value">High</div>
                <div className="stat-label">Impact</div>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="benefit-card reveal">
            <div className="card-number">02</div>
            <div className="card-label">Growth</div>
            <h3 className="card-title">Lead generation</h3>
            <p className="card-description">How brokers can build a more consistent pipeline instead of depending only on portals, builders, or unpredictable referrals.</p>
            <div className="card-stats">
              <div className="stat">
                <div className="stat-value">02</div>
                <div className="stat-label">Key Topic</div>
              </div>
              <div className="stat">
                <div className="stat-value">Core</div>
                <div className="stat-label">Need</div>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="benefit-card reveal">
            <div className="card-number">03</div>
            <div className="card-label">Positioning</div>
            <h3 className="card-title">Getting mandates</h3>
            <p className="card-description">What helps brokers move from chasing inventory to securing stronger mandates and better quality opportunities.</p>
            <div className="card-stats">
              <div className="stat">
                <div className="stat-value">03</div>
                <div className="stat-label">Key Topic</div>
              </div>
              <div className="stat">
                <div className="stat-value">Deal</div>
                <div className="stat-label">Quality</div>
              </div>
            </div>
          </div>

          {/* Card 4 */}
          <div className="benefit-card reveal">
            <div className="card-number">04</div>
            <div className="card-label">Brand</div>
            <h3 className="card-title">Building personal brand</h3>
            <p className="card-description">How brokers can build trust-based visibility that improves referrals, client confidence, and long-term market positioning.</p>
          </div>

          {/* Card 5 */}
          <div className="benefit-card reveal">
            <div className="card-number">05</div>
            <div className="card-label">Scale</div>
            <h3 className="card-title">Scaling brokerage business</h3>
            <p className="card-description">How to think beyond one-off deals and build systems, process discipline, and consistency for sustainable brokerage growth.</p>
          </div>
        </div>
      </section>

      {/* MENTOR SECTION - LEFT ALIGNED */}
      <section className="mentor-section" id="mentor">
        <div className="mentor-container">
          <div className="mentor-image-wrapper mentor-image-wrapper-desktop">
            <div className="mentor-frame"></div>
            <div className="mentor-badge">Industry Titan</div>
            <img src="/BMP.jpg" alt="Dr. BM Pounacha" className="mentor-image" />
          </div>
          <div className="mentor-content">
            <div className="mentor-label">Distinguished Leadership</div>
            <h2 className="mentor-name">Dr. BM<br /> Pounacha</h2>
            <div className="mentor-image-wrapper mentor-image-wrapper-mobile">
              <div className="mentor-frame"></div>
              <div className="mentor-badge">Industry Titan</div>
              <img src="/BMP.jpg" alt="Dr. BM Pounacha" className="mentor-image" />
            </div>
            <p className="mentor-description">Dr. BM Pounacha distills two decades of luxury real estate leadership into modern, actionable playbooks that elevate Douglas & McCartney partners to unprecedented heights.</p>
            <p className="mentor-description">His mentorship blends strategic clarity, brand positioning, and hands-on deal coaching so your brokerage launches with confidence and scales with precision and grace.</p>
            <div className="mentor-stats">
              <div className="mentor-stat">
                <div className="mentor-stat-value">20+</div>
                <div className="mentor-stat-label">Years Experience</div>
              </div>
              <div className="mentor-stat">
                <div className="mentor-stat-value">12,000+</div>
                <div className="mentor-stat-label">Proteges Trained</div>
              </div>
            </div>
            <blockquote className="mentor-quote">
              Success in luxury real estate isn't about working harder—it's about working smarter with systems that compound your efforts over time.
            </blockquote>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION - LEFT ALIGNED */}
      <section className="process-section" id="process">
        <div className="process-header">
          <span className="overline">Access Flow</span>
          <h1 className="benefits-title">Three Steps to <br></br>Join the Webinar</h1>
          <p className="benefits-subtitle">Keep the free ebook offer in front, but make the webinar the primary decision. The page now drives users toward requesting a seat first.</p>
        </div>
        <div className="process-timeline">
          <div className="timeline-line"></div>
          <div className="process-steps">
            {/* Step 1 */}
            <div className="process-step reveal">
              <div className="step-content">
                <div className="step-label">Act One</div>
                <h3 className="step-title">Request Access</h3>
                <p className="step-description">Submit your details, choose your preferred session date, and request webinar access. The free ebook is included with your request.</p>
              </div>
              <div className="step-number">I</div>
              <div className="step-empty"></div>
            </div>
            {/* Step 2 */}
            <div className="process-step reveal">
              <div className="step-empty"></div>
              <div className="step-number">II</div>
              <div className="step-content">
                <div className="step-label">Act Two</div>
                <h3 className="step-title">Receive Confirmation</h3>
                <p className="step-description">Our team reviews requests and shares webinar confirmation details for the selected session, subject to seat availability.</p>
              </div>
            </div>
            {/* Step 3 */}
            <div className="process-step reveal">
              <div className="step-content">
                <div className="step-label">Act Three</div>
                <h3 className="step-title">Attend & Apply</h3>
                <p className="step-description">Attend the private 1-hour webinar, learn the broker growth framework, and apply the insights to improve client quality and consistency.</p>
              </div>
              <div className="step-number">III</div>
              <div className="step-empty"></div>
            </div>
          </div>
        </div>
      </section>

      {/* INCENTIVES SECTION */}
      <section className="incentives-section" id="incentives">
        <div className="incentives-header">
          <span className="overline">Participant Incentives</span>
          <h2 className="benefits-title">Why Attend Live</h2>
          <p className="benefits-subtitle">
            Seats are limited. Live attendees get bonus incentives, priority Q&amp;A, and free study material.
          </p>
        </div>

        <div className="incentives-grid">
          <div className="incentive-card incentive-card-featured reveal">
            <div className="incentive-eyebrow">
              <div className="incentive-label">Bonus Draw</div>
              <div className="incentive-badge">Seats Limited</div>
            </div>
            <h3 className="incentive-title">
              Win a private 1-hour consulting session with Dr. B M Pounacha
            </h3>
            <p className="incentive-description">
              One real estate professional will receive an exclusive 1:1 strategy session with Dr. B.M Pounacha.
            </p>
          </div>

          <div className="incentive-card reveal">
            <div className="incentive-label">Participant-Only</div>
            <h3 className="incentive-title">Participant-only future growth consideration</h3>
            <p className="incentive-description">
              Qualified attendees may receive participant-only advantages for future growth conversations, when relevant.
            </p>
          </div>

          <div className="incentive-card reveal">
            <div className="incentive-label">Live Access</div>
            <h3 className="incentive-title">Priority Q&amp;A on real broker roadblocks</h3>
            <p className="incentive-description">
              Ask practical questions and get direct guidance during the session.
            </p>
          </div>

          <div className="incentive-card reveal">
            <div className="incentive-label">Study Material</div>
            <h3 className="incentive-title">Free ebook + study material + action priorities</h3>
            <p className="incentive-description">
              Get the free ebook, study material, and a clearer action plan after the webinar.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION - LEFT ALIGNED */}
      <section className="contact-section" id="contact">
        <div className="contact-container">
          {/* LEFT SIDE - INFO */}
          <div className="contact-info">
            <div className="contact-label">Final Invite | Limited Seats</div>
            <h2 className="contact-title">Request Webinar Access Before Seats Close</h2>
            <p className="contact-description">
              This is a free private webinar for serious real estate brokers. Submit your request now to secure a seat for March 3 or March 10. The ebook can be downloaded separately.
            </p>
            <div className="contact-details">
              <div className="contact-detail">
                <div className="contact-detail-label">Sessions</div>
                <div className="contact-detail-value">
                  March 3, 2026 - 4:00 PM<br />
                  March 10, 2026 - 4:00 PM
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-detail-label">Seat Status</div>
                <div className="contact-detail-value">
                  Limited seats<br />
                  Priority for serious brokers
                </div>
              </div>
              <div className="contact-detail contact-detail-further-info">
                <div className="contact-detail-label">For Further Information</div>
                <div className="contact-detail-value">
                  <div className="contact-further-intro">
                    For further details, please contact:
                  </div>

                  <div className="contact-further-groups">
                    <div className="contact-person-card">
                      <div className="contact-person-name">Masi Khan</div>
                      <div className="contact-person-role">
                        Vice President - Franchise Development
                      </div>

                      <div className="contact-person-line">
                        <span className="contact-person-line-label">Mobile</span>
                        <a
                          href="tel:+919535298949"
                          className="contact-detail-inline-link contact-person-link"
                        >
                          +91 9535298949
                        </a>
                      </div>

                      <div className="contact-person-line">
                        <span className="contact-person-line-label">LinkedIn</span>
                        <a
                          href="https://www.linkedin.com/in/masikhan-franchiseb26/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="contact-detail-inline-link contact-person-link"
                        >
                          Linkedin Profile
                        </a>
                      </div>
                    </div>

                    <div className="contact-company-card">
                      <div className="contact-company-title">Company Contact</div>
                      <div className="contact-person-line">
                        <span className="contact-person-line-label">Email</span>
                        <a
                          href={
                            'mailto:partnerships@douglasmccartney.com' +
                            '?subject=' +
                            encodeURIComponent('Webinar Inquiry - Douglas & McCartney') +
                            '&body=' +
                            encodeURIComponent(
                              'Hello Team,\n\nI would like more information about the private webinar for real estate brokers.\n\nName:\nPhone:\nPreferred Session:\n\nThank you.'
                            )
                          }
                          className="contact-detail-inline-link contact-person-link"
                        >
                          partnerships@douglasmccartney.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* RIGHT SIDE - FORM */}
          <div className="contact-form-wrapper">
            <div className="form-ornament top-right"></div>
            <div className="form-ornament bottom-left"></div>
            <form className="contact-form" id="contactForm" onSubmit={handleWebinarContactSubmit}>
              <input type="hidden" name="campaign" value="webinar-ebook-broker-invite" />
              <input type="hidden" name="page" value="/webinar" />
              <input type="hidden" id="lastName" name="lastName" defaultValue="" />
              <input type="hidden" id="message" name="message" defaultValue="Webinar access request via /webinar landing page." />
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="firstName">
                    Full Name <span className="required-star" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="form-input"
                    placeholder="Enter your full name"
                    autoComplete="name"
                    inputMode="text"
                    maxLength={80}
                    pattern={FULL_NAME_PATTERN_HTML}
                    title={FULL_NAME_VALIDATION_MESSAGE}
                    onInput={(event) => clearFieldError(event.target)}
                    onBlur={(event) => {
                      event.target.value = normalizeFullName(event.target.value);
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="phone">
                    Mobile / WhatsApp Number <span className="required-star" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input"
                    placeholder="+91 XXX XXX XXXX"
                    autoComplete="tel-national"
                    inputMode="numeric"
                    pattern={PHONE_PATTERN_HTML}
                    title={PHONE_VALIDATION_MESSAGE}
                    maxLength={16}
                    onInput={(event) => {
                      clearFieldError(event.target);
                      event.target.value = event.target.value.replace(/[^\d+\s-]/g, '');
                    }}
                    onBlur={(event) => {
                      const formatted = formatIndianMobile(event.target.value);
                      event.target.value = formatted || event.target.value.trim();
                    }}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    Email Address <span className="required-star" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    placeholder="your@email.com"
                    autoComplete="email"
                    onInput={(event) => clearFieldError(event.target)}
                    onBlur={(event) => {
                      event.target.value = normalizeEmail(event.target.value);
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="interest">
                    Preferred Session (Limited Seats) <span className="required-star" aria-hidden="true">*</span>
                  </label>
                  <select id="interest" name="interest" className="form-select" required>
                    <option value="">Select your preferred session</option>
                    <option value="march-3-2026-4pm">March 3, 2026 - 4:00 PM</option>
                    <option value="march-10-2026-4pm">March 10, 2026 - 4:00 PM</option>
                    <option value="either-session">Either session works</option>
                    <option value="waitlist">Waitlist me if sessions are full</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="experience">Broker Experience</label>
                <select id="experience" name="experience" className="form-select">
                  <option value="">Select your experience level</option>
                  <option value="0-2">0-2 Years</option>
                  <option value="3-5">3-5 Years</option>
                  <option value="6-10">6-10 Years</option>
                  <option value="10+">10+ Years</option>
                </select>
              </div>
              <p style={{
                marginTop: '-4px',
                marginBottom: '18px',
                color: 'var(--grey)',
                fontSize: '12px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                lineHeight: 1.5,
              }}>
                Seats are limited. Webinar access is confirmed only after review.
              </p>
              <button type="submit" className="form-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Reserve Free Webinar Seat'}
              </button>
              
              {/* Status Messages */}
              {submitStatus === 'error' && (
                <div style={{
                  marginTop: '20px',
                  padding: '20px',
                  background: 'rgba(231, 76, 60, 0.1)',
                  border: '1px solid rgba(231, 76, 60, 0.3)',
                  color: '#e74c3c',
                  textAlign: 'center',
                  fontSize: '15px',
                  fontWeight: '500',
                }}>
                  Oops! Something went wrong. Please try again or contact us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer-section">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <img 
                src={isLightMode ? "/d_logo.png" : "/Douglas2.png"} 
                alt="Douglas & McCartney" 
                className="footer-logo-img"
              />
              <p className="footer-tagline">A structured real estate advisory platform.</p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-title">Company</h4>
                <a href="#benefits" onClick={(e) => { e.preventDefault(); scrollToSection('#benefits'); }} className="footer-link">Why Attend</a>
                <a href="#mentor" onClick={(e) => { e.preventDefault(); scrollToSection('#mentor'); }} className="footer-link">Speaker</a>
                <a href="#process" onClick={(e) => { e.preventDefault(); scrollToSection('#process'); }} className="footer-link">How It Works</a>
                <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('#contact'); }} className="footer-link">Request Access</a>
              </div>
              
              <div className="footer-column">
                <h4 className="footer-title">Legal</h4>
                <button onClick={() => window.open('/terms', '_blank')} className="footer-link">Terms & Conditions</button>
                <button onClick={() => window.open('/privacy', '_blank')} className="footer-link">Privacy Policy</button>
                <button onClick={() => window.open('/cancellation-refund', '_blank')} className="footer-link">Cancellation & Refund</button>
                <button onClick={() => window.open('/contact', '_blank')} className="footer-link">Contact Us</button>
                <button onClick={() => window.open('/about', '_blank')} className="footer-link">About Douglas & McCartney</button>
              </div>
              
              <div className="footer-column">
                <h4 className="footer-title">Follow Us</h4>
                <div className="footer-social">
                  <a
                    href="https://www.linkedin.com/company/douglas-mccartney/posts/?feedView=all"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    title="LinkedIn"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                    </svg>
                  </a>
                  <a
                    href="https://www.douglasmccartney.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    title="Official Website"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M3 12h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <path d="M12 3c2.8 2.6 4 5.6 4 9s-1.2 6.4-4 9c-2.8-2.6-4-5.6-4-9s1.2-6.4 4-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-divider"></div>
          
          <div className="footer-bottom">
            <p className="footer-copyright">© {new Date().getFullYear()} Douglas & McCartney. All rights reserved.</p>
            <p className="footer-location">Bangalore, Karnataka, India</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebinarPage;







